/**
 * "Your Clinical Twin", mockup 6 of 23 September 2026.
 *
 * Top to bottom as drawn: how many similar patients, what a Clinical Twin is,
 * the plan the comparison is for, what happened to patients like you, your
 * progress against theirs, and what that means for you.
 *
 * The rule that governs every number: below the patient-facing minimum of 50
 * matched patients, "Limited MCOS data" takes the place of each figure. Not a
 * smaller number and not an average from the thin cohort, because either
 * would read as a real result. The server enforces this (the payload carries
 * no figure below the minimum); this screen only draws what it is given.
 *
 * This is the live comparison, recomputed on every visit. The treatment plan
 * page shows the evidence frozen when the clinician chose, and says so.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  Card,
  DISPLAY,
  DISPLAY_MID,
  IconTile,
  PageTitle,
  PortalEmpty,
  PortalError,
  PortalLoading,
  RangeSelect,
  TONE,
  formatLbs,
  formatNumber,
  usePortalQuery,
  type RangeOption,
} from './shell';
import {
  BarsLine,
  Bulb,
  Capsule,
  Clipboard,
  Dumbbell,
  HeartLine,
  LeafLine,
  LeafSolid,
  Scale,
  Syringe,
  Trend,
  Trophy,
  Users,
} from './icons';

/* ------------------------------------------------------------------ *
 * What the API sends (GET /portal/twin)
 * ------------------------------------------------------------------ */

interface TwinTreatment {
  therapyId: string;
  name: string;
  category: string;
  route: string | null;
  dose: string | null;
  frequency: string | null;
  goalTags: string[];
}

interface Timepoints {
  baseline: number | null;
  month3: number | null;
  month6: number | null;
}

interface Trajectory {
  metric: 'weight' | 'a1c';
  unit: string;
  you: Timepoints;
  cohort: Timepoints | null;
}

export interface TwinView {
  hasPlan: boolean;
  cohortSize: number | null;
  minCohort: number;
  limitedReason: string | null;
  windowMonths: number;
  treatments: TwinTreatment[];
  stats: {
    avgWeightChangeLb: number | null;
    a1cBelowPrediabetesPct: number | null;
    continuationPct: number | null;
  } | null;
  trajectories: Trajectory[];
  comparison: {
    metric: 'weight';
    at: 'month3' | 'month6';
    yourChange: number;
    cohortChange: number;
    ahead: boolean;
  } | null;
  meaning: string;
  disclaimer: string;
}

const LIMITED = 'Limited MCOS data';

type Period = 'm6' | 'm3';
const PERIODS: Array<RangeOption<Period>> = [
  { value: 'm6', label: 'Last 6 Months' },
  { value: 'm3', label: 'Last 3 Months' },
];

type Metric = 'weight' | 'a1c';
const METRICS: Array<RangeOption<Metric>> = [
  { value: 'weight', label: 'Weight Loss' },
  { value: 'a1c', label: 'A1c' },
];

/**
 * Which drawing a plan column gets, as mockup 6 assigns them: the GLP-1 is
 * the syringe, a peptide for muscle, recovery or body composition is the
 * dumbbell, any other peptide is the capsule, and vitamins are the leaf.
 */
export function twinIcon(treatment: Pick<TwinTreatment, 'category' | 'goalTags'>): 'syringe' | 'dumbbell' | 'capsule' | 'leaf' {
  const category = treatment.category.toLowerCase();
  if (category.includes('glp')) return 'syringe';
  if (category.includes('peptide')) {
    const strength = ['lean muscle', 'recovery', 'performance', 'body composition'];
    return treatment.goalTags.some((tag) => strength.includes(tag)) ? 'dumbbell' : 'capsule';
  }
  return 'leaf';
}

function TreatmentGlyph({ treatment }: { treatment: TwinTreatment }) {
  const kind = twinIcon(treatment);
  if (kind === 'syringe') return <Syringe size={22} strokeWidth={1.7} />;
  if (kind === 'dumbbell') return <Dumbbell size={22} strokeWidth={1.7} />;
  if (kind === 'capsule') return <Capsule size={22} strokeWidth={1.7} />;
  return <LeafSolid size={21} />;
}

/**
 * The two muted lines under a treatment: its dose and how often, as the
 * clinician recorded them. With no dose recorded the route takes the first
 * line; a dose is never made up.
 */
export function doseLines(treatment: Pick<TwinTreatment, 'dose' | 'frequency' | 'route'>): string[] {
  const first = treatment.dose ?? treatment.route;
  return [first, treatment.frequency].filter((line): line is string => Boolean(line && line.trim()));
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export function PortalTwin() {
  const { data, isLoading, error, refetch } = usePortalQuery<TwinView>('twin', '/twin');
  const [period, setPeriod] = useState<Period>('m6');

  if (isLoading) return <PortalLoading />;
  if (error) {
    return (
      <PortalError
        message={error instanceof Error ? error.message : 'Could not load your comparison'}
        retry={() => void refetch()}
      />
    );
  }
  if (!data) return null;

  const title = (
    <PageTitle
      title="Your Clinical Twin"
      mark
      size={30}
      nowrap
      subtitle="Real patients. Real results. A clearer path for you."
      aside={
        data.hasPlan ? (
          <RangeSelect label="Comparison period" value={period} options={PERIODS} onChange={setPeriod} />
        ) : undefined
      }
    />
  );

  if (!data.hasPlan) {
    return (
      <>
        {title}
        <PortalEmpty title="Not available yet" body={data.limitedReason ?? data.meaning} />
      </>
    );
  }

  const count = data.cohortSize;

  return (
    <div className="space-y-[8px] pb-1">
      {title}

      {/* 1. How many similar patients, and what a Clinical Twin is. */}
      <section className="rounded-[9px] border px-[9px] pb-[9px] pt-[9px]" style={{ background: '#f8faf9', borderColor: '#e3ebe6' }}>
        <div className="flex items-center gap-[10px]">
          <span
            aria-hidden="true"
            className="grid h-[62px] w-[62px] shrink-0 place-items-center rounded-full"
            style={{ background: 'radial-gradient(circle at 35% 30%, #fdf0c8 0%, #fbe7b5 45%, #f6d98f 100%)', color: '#5e3a01' }}
          >
            <Users size={34} strokeWidth={1.6} />
          </span>
          <div className="min-w-0 shrink-0 text-center">
            <p
              className="font-display font-semibold leading-none"
              style={{
                ...DISPLAY_MID,
                color: '#603b07',
                fontSize: count !== null ? (count >= 10_000 ? 32 : 40) : 17,
                letterSpacing: '-0.015em',
              }}
            >
              {count !== null ? formatNumber(count) : LIMITED}
            </p>
            <p className="mt-[5px] text-[11.5px] font-semibold uppercase leading-none tracking-[-0.005em]" style={{ color: '#5e3f11' }}>
              Similar patients
            </p>
          </div>
          <div className="min-w-0 flex-1 border-l pl-[10px]" style={{ borderColor: '#d9e3dd' }}>
            <p className="font-display text-[12.5px] font-semibold leading-tight" style={{ color: TONE.heading, fontVariationSettings: '"opsz" 24' }}>
              What is a Clinical Twin?<sup className="ml-[1px] text-[8px] font-medium">™</sup>
            </p>
            <p className="mt-[4px] text-[10px] leading-[1.36]" style={{ color: '#4f5b6e' }}>
              {count !== null
                ? `MCOS matches your clinical profile, labs, goals, and treatment with ${formatNumber(count)} real patients who are like you to show what happened to patients with a similar health profile.`
                : `MCOS matches your clinical profile, labs, goals, and treatment with real patients who are like you. It shows a comparison once at least ${data.minCohort} of them have completed your exact plan.`}
            </p>
          </div>
        </div>
        <div className="mt-[9px] flex items-center justify-center gap-[9px] rounded-[7px] px-3 py-[6px]" style={{ background: '#eaf3ee' }}>
          <span className="shrink-0 text-forest">
            <LeafLine size={17} strokeWidth={1.7} />
          </span>
          <p className="text-[10.5px] leading-tight" style={{ color: '#173b30' }}>
            Real data from real patients. Real insights for your journey.
          </p>
        </div>
      </section>

      {/* 2. The plan the comparison is for. */}
      <Card className="px-[13px] pb-[12px] pt-[12px]">
        <CardTitle
          icon={<Clipboard size={25} strokeWidth={1.6} />}
          title="Your Current Treatment Plan"
          subtitle="This is the treatment plan used for your Clinical Twin comparison."
        />
        <div
          className="mt-[12px] grid"
          style={{ gridTemplateColumns: `repeat(${Math.min(3, Math.max(1, data.treatments.length))}, minmax(0, 1fr))` }}
        >
          {data.treatments.slice(0, 3).map((treatment, index) => (
            <div
              key={treatment.therapyId}
              className={`flex min-w-0 flex-col items-center px-1 text-center ${index > 0 ? 'border-l' : ''}`}
              style={{ borderColor: '#e6e8e7' }}
            >
              <IconTile size={38} round>
                <TreatmentGlyph treatment={treatment} />
              </IconTile>
              <p className="mt-[8px] line-clamp-2 w-full break-words text-[12px] font-semibold leading-tight" style={{ color: TONE.heading }}>
                {treatment.name}
              </p>
              {doseLines(treatment).map((line) => (
                <p key={line} className="mt-[3px] text-[11px] leading-tight" style={{ color: '#5b6985' }}>
                  {line}
                </p>
              ))}
            </div>
          ))}
        </div>
        {data.treatments.length > 3 && (
          <p className="mt-[10px] text-center text-[11px]" style={{ color: TONE.faint }}>
            And {data.treatments.length - 3} more on your plan.
          </p>
        )}
      </Card>

      {/* 3. What happened to patients like you. */}
      <Card className="px-[13px] pb-[12px] pt-[12px]">
        <CardTitle
          icon={<BarsLine size={25} strokeWidth={1.6} />}
          title="Patients Like You: What Happened"
          subtitle={
            count !== null
              ? `Average results from ${formatNumber(count)} similar patients on a similar treatment plan.`
              : 'Average results appear once enough similar patients have completed your plan.'
          }
        />
        <div className="mt-[12px] grid grid-cols-3">
          <Stat
            icon={<Scale size={20} strokeWidth={1.6} />}
            value={
              data.stats?.avgWeightChangeLb != null
                ? `${data.stats.avgWeightChangeLb > 0 ? '+' : ''}${formatNumber(data.stats.avgWeightChangeLb, 1)} lbs`
                : null
            }
            caption={`Average weight ${data.stats?.avgWeightChangeLb != null && data.stats.avgWeightChangeLb > 0 ? 'change' : 'loss'} at ${data.windowMonths} months`}
          />
          <Stat
            icon={<HeartLine size={20} strokeWidth={1.7} />}
            value={data.stats?.a1cBelowPrediabetesPct != null ? `${data.stats.a1cBelowPrediabetesPct}%` : null}
            caption="Brought A1c below 5.7%"
            divider
          />
          <Stat
            icon={<Users size={20} strokeWidth={1.6} />}
            value={data.stats?.continuationPct != null ? `${data.stats.continuationPct}%` : null}
            caption={`Continued treatment at ${data.windowMonths} months`}
            divider
          />
        </div>
      </Card>

      {/* 4. Your progress against theirs. */}
      <ProgressVsCohort data={data} period={period} />

      {/* 5. What it means. */}
      <section className="flex gap-[12px] rounded-[9px] border px-[12px] py-[11px]" style={{ background: '#fdf8ee', borderColor: '#f2e7d0' }}>
        <span
          aria-hidden="true"
          className="grid h-[40px] w-[40px] shrink-0 place-items-center rounded-full"
          style={{ background: '#f6e3b4', color: '#6b4a1e' }}
        >
          <Bulb size={23} strokeWidth={1.6} />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-[14px] font-semibold leading-tight" style={{ ...DISPLAY_MID, color: TONE.heading }}>
            What This Means for You
          </h2>
          <p className="mt-[4px] text-[11px] leading-[1.42]" style={{ color: '#3f4c62' }}>
            {data.meaning}
          </p>
        </div>
      </section>

      <p className="mx-auto max-w-[340px] px-2 pb-1 pt-[6px] text-center text-[10px] leading-[1.5]" style={{ color: '#5b6985' }}>
        Your Clinical Twin is based on de-identified data from patients with similar clinical profiles. Results
        may vary. Always follow your clinician&apos;s recommendations.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Pieces
 * ------------------------------------------------------------------ */

function CardTitle({
  icon,
  title,
  subtitle,
  aside,
  size = 14.5,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  aside?: ReactNode;
  size?: number;
}) {
  return (
    <div className="flex items-start gap-[10px]">
      <span className="mt-[1px] shrink-0 text-forest">{icon}</span>
      <div className="min-w-0 flex-1">
        <h2 className="font-display font-semibold leading-tight" style={{ ...DISPLAY_MID, color: TONE.heading, letterSpacing: '-0.005em', fontSize: size }}>
          {title}
        </h2>
        {subtitle && (
          <p className="mt-[3px] text-[10px] leading-snug" style={{ color: '#4f5b6e' }}>
            {subtitle}
          </p>
        )}
      </div>
      {aside}
    </div>
  );
}

function Stat({ icon, value, caption, divider = false }: { icon: ReactNode; value: string | null; caption: string; divider?: boolean }) {
  return (
    <div className={`flex min-w-0 items-start gap-[7px] px-[5px] ${divider ? 'border-l' : ''}`} style={{ borderColor: '#e6e8e7' }}>
      <IconTile size={32} round>
        {icon}
      </IconTile>
      <div className="min-w-0">
        {value !== null ? (
          <p className="whitespace-nowrap font-display font-semibold leading-none" style={{ ...DISPLAY, color: TONE.heading, letterSpacing: '-0.01em', fontSize: 'clamp(14px, calc(19px - (390px - 100vw) * 0.15), 19px)' }}>
            {value}
          </p>
        ) : (
          <p className="text-[11px] font-semibold leading-tight" style={{ color: TONE.heading }}>
            {LIMITED}
          </p>
        )}
        <p className="mt-[4px] text-[10px] leading-[1.3]" style={{ color: '#5b6985' }}>
          {caption}
        </p>
      </div>
    </div>
  );
}

const POINTS: Array<{ key: keyof Timepoints; label: string }> = [
  { key: 'baseline', label: 'Baseline' },
  { key: 'month3', label: '3 Months' },
  { key: 'month6', label: '6 Months' },
];

/** Round a domain outwards to tidy ticks, as the mockup's 160 to 240 does. */
function niceDomain(values: number[], step: number): [number, number] {
  if (values.length === 0) return [0, step * 4];
  const low = Math.floor((Math.min(...values) - step / 2) / step) * step;
  const high = Math.ceil((Math.max(...values) + step / 2) / step) * step;
  return [low, Math.max(high, low + step * 2)];
}

function ProgressVsCohort({ data, period }: { data: TwinView; period: Period }) {
  const [metric, setMetric] = useState<Metric>('weight');
  const trajectory = data.trajectories.find((entry) => entry.metric === metric) ?? null;

  const rows = useMemo(() => {
    if (!trajectory) return [];
    const points = period === 'm3' ? POINTS.slice(0, 2) : POINTS;
    return points.map((point) => ({
      label: point.label,
      you: trajectory.you[point.key],
      cohort: trajectory.cohort ? trajectory.cohort[point.key] : null,
    }));
  }, [trajectory, period]);

  const values = rows.flatMap((row) => [row.you, row.cohort]).filter((value): value is number => value !== null);
  const step = metric === 'weight' ? 20 : 0.5;
  const domain = niceDomain(values, step);
  const ticks: number[] = [];
  for (let at = domain[0]; at <= domain[1] + 1e-9; at += step) ticks.push(Number(at.toFixed(2)));

  const youCount = rows.filter((row) => row.you !== null).length;
  const comparison = metric === 'weight' ? data.comparison : null;
  const unit = metric === 'weight' ? 'lbs' : '%';

  return (
    <Card className="px-[13px] pb-[12px] pt-[12px]">
      <CardTitle
        icon={<Trend size={25} strokeWidth={1.7} />}
        title="Your Progress vs. Patients Like You"
        size={12.2}
        aside={
          <RangeSelect label="What to compare" value={metric} options={METRICS} onChange={setMetric} className="mt-[-3px]" compact />
        }
      />

      <div className="mt-[10px] flex items-center justify-center gap-[20px] text-[11px]" style={{ color: '#3f4c62' }}>
        <span className="flex items-center gap-[6px]">
          <span aria-hidden="true" className="h-[9px] w-[9px] rounded-full bg-forest" />
          You
        </span>
        <span className="flex items-center gap-[6px]">
          <span aria-hidden="true" className="h-[9px] w-[9px] rounded-full" style={{ background: '#9aa9bf' }} />
          Similar Patients (Average)
        </span>
      </div>

      <div className="mt-[4px] flex items-stretch gap-[8px]">
        <div className="min-w-0 flex-1">
          {youCount === 0 ? (
            <p className="py-12 text-center text-[12px] leading-relaxed" style={{ color: TONE.body }}>
              {metric === 'weight'
                ? 'Your line starts with your first body composition scan on this plan.'
                : 'Your line starts with your first A1c on this plan.'}
            </p>
          ) : (
            <div className="h-[136px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={rows} margin={{ top: 8, right: 10, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="twin-you-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2f8a5f" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#2f8a5f" stopOpacity={0.06} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#ececea" strokeWidth={0.8} />
                  <XAxis
                    dataKey="label"
                    interval={0}
                    tickLine={false}
                    axisLine={{ stroke: '#d8d8d6' }}
                    tick={{ fontSize: 10, fill: '#4f5b6e' }}
                    tickMargin={7}
                    padding={{ left: 14, right: 14 }}
                    height={24}
                  />
                  <YAxis
                    domain={domain}
                    ticks={ticks}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10, fill: '#4f5b6e' }}
                    tickMargin={4}
                    width={44}
                    tickFormatter={(value: number) => (metric === 'weight' ? String(value) : value.toFixed(1))}
                    label={{
                      value: metric === 'weight' ? 'Weight (lbs)' : 'A1c (%)',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 4,
                      style: { fontSize: 10, fill: '#4f5b6e', textAnchor: 'middle' },
                    }}
                  />
                  <Tooltip
                    cursor={{ stroke: '#d8d8d6', strokeWidth: 1 }}
                    contentStyle={{ borderRadius: 8, border: `1px solid ${TONE.line}`, background: '#fff', fontSize: 12, padding: '6px 10px' }}
                    formatter={(value: number, name: string) => [
                      `${metric === 'weight' ? formatLbs(value) : value.toFixed(1)} ${unit}`,
                      name === 'you' ? 'You' : 'Similar patients (average)',
                    ]}
                  />
                  <Area
                    type="linear"
                    dataKey="you"
                    stroke="none"
                    fill="url(#twin-you-fill)"
                    connectNulls={false}
                    isAnimationActive={false}
                    activeDot={false}
                    tooltipType="none"
                  />
                  <Line
                    type="linear"
                    dataKey="cohort"
                    stroke="#9aa9bf"
                    strokeWidth={2}
                    dot={{ r: 3.8, fill: '#9aa9bf', stroke: '#9aa9bf', strokeWidth: 0 }}
                    activeDot={{ r: 4.5 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="linear"
                    dataKey="you"
                    stroke="#0b5345"
                    strokeWidth={2.2}
                    dot={{ r: 3.8, fill: '#0b5345', stroke: '#0b5345', strokeWidth: 0 }}
                    activeDot={{ r: 4.5 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
          {!trajectory?.cohort && youCount > 0 && (
            <p className="mt-[2px] text-center text-[10.5px]" style={{ color: TONE.faint }}>
              {data.cohortSize === null ? `${LIMITED}: no average line yet.` : 'No average line for this measure yet.'}
            </p>
          )}
        </div>

        {metric === 'weight' && <Callout comparison={comparison} limited={data.cohortSize === null} />}
      </div>
    </Card>
  );
}

/**
 * The mint callout beside the chart. It congratulates only when the patient
 * has actually lost more than the cohort average at the same point; anything
 * else is stated neutrally.
 */
function Callout({ comparison, limited }: { comparison: TwinView['comparison']; limited: boolean }) {
  const at = comparison?.at === 'month3' ? '3 months' : '6 months';
  return (
    <div className="flex w-[100px] shrink-0 flex-col items-center justify-center rounded-[8px] px-[8px] py-[10px] text-center" style={{ background: '#eaf4ee' }}>
      <span aria-hidden="true" className="grid h-[34px] w-[34px] place-items-center rounded-full" style={{ background: '#d9ebe0', color: '#0a3d30' }}>
        <Trophy size={20} strokeWidth={1.7} />
      </span>
      {comparison ? (
        <>
          <p className="mt-[7px] text-[11px] leading-tight" style={{ color: '#1f3a33' }}>
            {comparison.yourChange <= 0 ? "You're down" : "You're up"}
          </p>
          <p className="mt-[3px] whitespace-nowrap font-display text-[21px] font-semibold leading-none" style={{ ...DISPLAY, color: '#0a3d30' }}>
            {formatLbs(Math.abs(comparison.yourChange))} lbs
          </p>
          <p className="mt-[5px] text-[11px] leading-[1.3]" style={{ color: '#1f3a33' }}>
            vs. {formatLbs(Math.abs(comparison.cohortChange))} lbs average{comparison.at === 'month3' ? ` at ${at}` : ''}
          </p>
          {comparison.ahead && (
            <span className="mt-[8px] whitespace-nowrap rounded-full px-[7px] py-[4px] text-[10px] font-semibold leading-none" style={{ background: '#d4e8dc', color: '#0a3d30' }}>
              You&apos;re ahead!
            </span>
          )}
        </>
      ) : (
        <p className="mt-[8px] text-[10.5px] leading-snug" style={{ color: '#1f3a33' }}>
          {limited ? LIMITED : 'Your comparison appears after your next scan on this plan.'}
        </p>
      )}
    </div>
  );
}
